import { FC, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { Link, useNavigate } from 'react-router-dom';
import {
  apiClient,
  getToken,
  getOrgId,
  getRole,
  UserRole,
} from '@devd-client/api';
import { NewDataGrid } from '../../../NewGridTable/NewDataGrid';
import {
  useDashboardStore,
  useDetailsPageStore,
} from '../../../hooks/useDashboardStore';
import { useGlobalState } from '../../../hooks/useGlobalState';
import { useMetricSidebarStore } from '../../../hooks/useMetricSidebarStore';
import { MetricModalLoading } from './DefaultDetailsModal/MetricModalLoading';

interface ContributorsDetailsModalProps {
  openDrawer: boolean;
  handleDrawerClose: () => void;
  team: string;
  contributorData: any;
  loading: boolean;
  repoTitle?: boolean;
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  startDate?: string;
  endDate?: string;
  filterBy?: any;
}

export const ContributorsDetailsModal: FC<ContributorsDetailsModalProps> = ({
  openDrawer,
  handleDrawerClose,
  team,
  contributorData,
  loading,
  repoTitle,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  startDate,
  endDate,
  filterBy,
}) => {
  const rows: any[] = useMemo(
    () => contributorData?.data || [],
    [contributorData?.data]
  );

  const columnHelper = createColumnHelper<any>();
  const { selected, selectedTeam } = useDashboardStore();
  const { setSelected, setActive } = useDetailsPageStore();
  const { closeSidebar } = useMetricSidebarStore();
  const navigate = useNavigate();
  const { globalState } = useGlobalState();
  const downloadAllowed = globalState?.dto?.downloadAllowed;

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        cell: (info) => {
          const name = info.getValue();
          const email = info.row.original?.email;

          return (
            <Flex align="center">
              <Avatar size="xs" name={name} />
              <Link to={email ? `/contributors/${email}` : '#'}>
                <Text
                  ml={2}
                  fontSize="sm"
                  fontFamily="heading"
                  color="primary.500"
                  fontWeight="medium"
                  textDecoration="underline"
                  noOfLines={1}
                >
                  {name}
                </Text>
              </Link>
            </Flex>
          );
        },
        header: 'Name',
        enableSorting: false,
      }),
      columnHelper.accessor('email', {
        cell: (info) => (
          <Link to={`/contributors/${info.getValue()}`}>
            <Text
              ml={2}
              fontSize="sm"
              fontFamily="heading"
              color="text.secondary"
              fontWeight="medium"
              textDecoration="underline"
              noOfLines={1}
            >
              {info.getValue()}
            </Text>
          </Link>
        ),
        header: 'Email',
        enableSorting: false,
      }),
    ],
    [contributorData?.data]
  );

  const [pending, setPending] = useState(false);

  const download = (data: any) => {
    const blob = new Blob([data], { type: 'text/csv' });
    const url = window?.URL?.createObjectURL(blob);
    const a = document?.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute(
      'download',
      `${getOrgId()} - ${team === '' ? 'Organisation' : team}`
    );
    a.click();
  };

  const downloadCSV = async () => {
    setPending(true);
    apiClient(`/v1/csv/contributors`, {
      method: 'POST',
      headers: {
        authorization: `bearer ${getToken()}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        orgId: getOrgId(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        startDate: startDate || selected.startDate,
        endDate: endDate || selected.endDate,
        teamId: team || selectedTeam.value,
      }),
    })
      .then((res: any) => {
        setPending(false);
        download(res);
      })
      .catch(() => {
        setPending(false);
      });
  };

  const subHeading = repoTitle
    ? `Contributors for selected Repository`
    : `Contributors Details${team === '' ? '' : ` for Team "${team}"`}`;

  const totalCount = contributorData?.totalDBRowCount;

  return (
    <Modal
      isOpen={openDrawer}
      onClose={handleDrawerClose}
      size="4xl"
      isCentered
      scrollBehavior="inside"
      autoFocus={false}
    >
      <ModalOverlay />
      <ModalContent minW="4xl" maxW="4xl">
        <ModalHeader pb={2}>
          <Flex justify="space-between" align="flex-start" gap={4}>
            <Stack spacing={1}>
              <Text
                fontFamily="heading"
                fontSize="lg"
                fontWeight="bold"
                color="text.primary"
              >
                {subHeading}
              </Text>
              {totalCount !== undefined && (
                <Text
                  fontFamily="heading"
                  fontSize="sm"
                  color="text.secondary"
                  fontWeight="medium"
                >
                  {totalCount} {totalCount > 1 ? 'contributors' : 'contributor'}
                </Text>
              )}
            </Stack>
            <Flex align="center" gap={2} mr={6}>
              <Tooltip
                hasArrow
                label={!downloadAllowed ? 'Upgrade plan to download data' : ''}
                isDisabled={downloadAllowed}
              >
                <Button
                  variant="tertiary"
                  size="sm"
                  h="32px"
                  px={4}
                  borderRadius="full"
                  onClick={downloadCSV}
                  isLoading={pending}
                  isDisabled={!downloadAllowed}
                  minW="120px"
                >
                  Download Data
                </Button>
              </Tooltip>
              {getRole() !== UserRole.EMPLOYEE &&
                globalState?.user?.dto?.modules?.contributorSearch !==
                  false && (
                  <Button
                    variant="primary"
                    size="sm"
                    h="32px"
                    px={4}
                    borderRadius="full"
                    onClick={() => {
                      setSelected({
                        id: 6,
                        startDate: startDate as string,
                        endDate: endDate as string,
                        duration: filterBy?.sprintId ? 'Sprint' : 'Custom',
                        sprintId: filterBy?.sprintId || '',
                      });
                      setActive(filterBy?.sprintId ? 'Sprint' : 'Custom');
                      navigate(
                        `/contributors?team=${encodeURIComponent(team)}`,
                        { state: { moreDetails: true } }
                      );
                      closeSidebar();
                    }}
                  >
                    More Details
                  </Button>
                )}
            </Flex>
          </Flex>
        </ModalHeader>
        <ModalCloseButton mt={2} />
        <ModalBody pb={6}>
          <Stack spacing={4}>
            {loading && !contributorData?.data ? (
              <MetricModalLoading />
            ) : rows && rows.length > 0 ? (
              <Box
                borderWidth="1px"
                borderRadius="md"
                overflow="hidden"
                w="100%"
              >
                <NewDataGrid
                  data={rows}
                  columns={columns}
                  showLoader={false}
                  skeletonLoader={false}
                  sticky="none"
                  useMaxH
                  showGridLines
                  hasNextPage={hasNextPage}
                  fetchNextPage={fetchNextPage}
                  isFetchingNextPage={isFetchingNextPage}
                  infiniteScroll={true}
                />
              </Box>
            ) : (
              <Box
                borderWidth="1px"
                borderRadius="md"
                overflow="hidden"
                w="100%"
                minH="200px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Text
                  fontFamily="heading"
                  fontSize="sm"
                  color="text.secondary"
                  fontWeight="medium"
                >
                  No data found
                </Text>
              </Box>
            )}
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ContributorsDetailsModal;
